import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { api } from '../config';

const Devices = () => {
  const theme = useTheme();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchDevices = async () => {
    try {
      console.log('Fetching devices');
      setLoading(true);
      const response = await api.get('/devices');
      setDevices(response.data);
      setError(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error fetching devices: ' + error.message,
        severity: 'error'
      });
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
            Devices
          </Typography>
          <Tooltip title="Refresh devices">
            <IconButton
              onClick={fetchDevices}
              color="primary"
              sx={{
                transition: theme.transitions.create(['transform']),
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            sx={{
              p: 3,
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 2,
            }}
          >
            <Typography variant="body1">{error}</Typography>
          </Paper>
        ) : devices.length === 0 ? (
          <Alert severity="info">No devices found. Devices will appear here once they are registered.</Alert>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Hostname</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Serial</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Seen (Google)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last User (Google)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id} hover>
                    <TableCell>{device.hostname || '-'}</TableCell>
                    <TableCell>{device.serial || '-'}</TableCell>
                    <TableCell>{device.lastSeenGoogle || '-'}</TableCell>
                    <TableCell>{device.lastUserGoogle || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Devices;
